<template>
    <div />
</template>

<script>
    import { toRaw } from 'vue';
    import {Vector3} from "three";
    import * as utils from "threebox-plugin/src/utils/utils";
    import {loadObj} from "./loadObj";
    import {setMercator, setOriginProjection} from "../../utils/tbUtils";

    // 三维模型加载到mapbox图层
    export default {
        name: "Object3d",
        //模型初始路径，旋转角，尺寸
        props: {
            objectConfig:{
                required: true, // 3D物体配置
                default: {
                    origin: [0, 0], //初始坐标，数组长度为3直接使用，数组长度为2会计算地表高度填充
                    originShifting: [0, 0, 0],  //考虑scale后的坐标偏移
                    type: 'glb', //3D格式 glb/mtl（这个类型就是obj）/gltf/fbx/dae
                    model: '', //3D文件名
                    rotation: { x: 90, y: 0, z: 0 }, //不计算startRotation时另模型正面向上面向南方
                    scale: 1, //缩放
                    startRotation: { x: 0, y: 0, z: 0 }//初始模型朝向
                }
            },
            currentPosition:{
                required: true, // 当前位置，组件会根据每帧改变的当前位置自动令模型自动调整朝向移动
                default: [0, 0, 0]
            },
            map:{
                required: true // mapbox对象
            },
            tb:{
                required: true // threebox对象
            }
        },
        data(){
            return{
                id:'',
                model:null
            }
        },
        methods:{
            setPositionWithEvent(model, position) { // 变更模型位置，此函数实现用于兼容模型位移与模型本身的动画model.playAnimation
                //使用_setObject更新位置和角度可以触发事件播放动画，使用set或setCoords会导致播放不出动画
                model._setObject({position: position});
            },

            quaternionRotationWithEvent(model, pt0, pt1) { // 变更模型朝向角度，此函数实现用于兼容模型位移与模型本身的动画model.playAnimation
                //代码取自model.followPath（实现于AnimationManager.update），此处相当于用新旧值做0间隔动画
                //保证模型初始角度面向地图南方（y正方向）
                const pts = utils.lnglatsToWorld([pt0, pt1]);
                const tangent = new Vector3();
                tangent.copy( pts[1] ).sub( pts[0] ).normalize();
                const axis = new Vector3(0, 0, 0);
                const up = new Vector3(0, 1, 0);
                axis.crossVectors(up, tangent).normalize();
                const radians = Math.acos(up.dot(tangent));
                //使用_setObject更新位置和角度可以触发事件播放动画，使用set或setCoords会导致播放不出动画
                model._setObject({ quaternion: [axis, radians] });
            },

            setPointElevation(point){
                if(point.length===2){
                    //高度获取
                    let elevation = this.map.queryTerrainElevation({lng: point[0], lat: point[1]}, { exaggerated: false });
                    if(!elevation){
                        elevation = 0;
                    }
                    point = [point[0], point[1], elevation];
                }
                // console.log(point)
                // console.log(this.objectConfig.originShifting)
                point = [point[0] + this.objectConfig.originShifting[0],
                    point[1] + this.objectConfig.originShifting[1],
                    point[2] + this.objectConfig.originShifting[2]];
                return point;
            },

            setObject3D(){
                const _this = this;
                const tb = this.tb;
                // 飞行物自定义图层
                this.map.addLayer({
                    id: this.id,
                    type: 'custom',
                    renderingMode: '3d',

                    onAdd(){
                        const origin = _this.setPointElevation(_this.objectConfig.origin);
                        //模型参数
                        const options = {
                            type: _this.objectConfig.type,
                            obj: _this.objectConfig.model + "." + _this.objectConfig.type,
                            units: 'meters',
                            scale: _this.objectConfig.scale,
                            rotation: _this.objectConfig.rotation,
                            anchor: 'top'
                        };

                        if(_this.objectConfig.type === 'mtl'){
                            options.obj = _this.objectConfig.model + ".obj";
                            options.mtl = _this.objectConfig.model + ".mtl";
                        }

                        //读取模型
                        loadObj(tb, options, function (model) {
                            // 模型初始位置
                            _this.model = model.setCoords(origin);

                            // 设置模型静止时角度，运动起来就会被覆盖
                            _this.model.setRotation(_this.objectConfig.startRotation);

                            _this.model.castShadow = true;
                            _this.model.selected = true;

                            tb.add(toRaw(_this.model));
                        });
                    },

                    render(){
                        //渲染
                        tb.update();
                    }
                });

            },
            removeObject3D(){
                if(this.model){
                    this.tb.remove(toRaw(this.model));
                }
                this.map.removeLayer(this.id);
            },
        },
        watch: {
            currentPosition(newData, oldData){
               if(this.model){
                   const p0 = this.setPointElevation(oldData);
                   const p1 = this.setPointElevation(newData);
                   //实时更新模型位置
                   this.setPositionWithEvent(this.model, p1);
                   //更新模型角度
                   this.quaternionRotationWithEvent(this.model, p0, p1);
               }
            }
        },
        mounted() {
            this.id = 'Object3d' + Math.random().toString(36);
            setMercator(this.map, this.id);

            // 初始化三维物体
            this.setObject3D();
        },
        destroyed() {
            this.removeObject3D();
            setOriginProjection(this.map, this.id);
        }
    }
</script>

<style scoped>

</style>